import { useEffect, useState } from 'react';
import { useStore } from '@nanostores/react';
import { motion, AnimatePresence } from 'framer-motion';
import { $selectedElement, $styleDeltas, type StyleDeltas } from '~/lib/stores/element-selector';
import { applyStyleEditDirect, applyTextEditDirect } from '~/lib/element-selector/direct-edit';
import { trackVisualEdit } from '~/lib/element-selector/analytics';
import { apiReadSandboxFile } from '~/lib/api/sandbox-files';
import { logStyleEdit, logStyleEditFailed, logTextEdit, logTextEditFailed } from '~/lib/stores/edit-history';
import { cssColorToHex } from '~/lib/element-selector/color-utils';
import { ColorField } from '~/components/workbench/ColorField';
import { classNames } from '~/utils/classNames';

interface Props {
  chatId: string;
  onReloadPreview: () => void;
}

const WEIGHTS = [
  { value: '300', label: 'Light' },
  { value: '400', label: 'Normal' },
  { value: '500', label: 'Medium' },
  { value: '600', label: 'Semibold' },
  { value: '700', label: 'Bold' },
  { value: '800', label: 'Extra Bold' },
];

const ALIGNS = [
  { value: 'left', icon: 'i-ph:text-align-left' },
  { value: 'center', icon: 'i-ph:text-align-center' },
  { value: 'right', icon: 'i-ph:text-align-right' },
  { value: 'justify', icon: 'i-ph:text-align-justify' },
];

const COLOR_SWATCHES = [
  { name: 'White', hex: '#FFFFFF' },
  { name: 'Black', hex: '#0A0A0A' },
  { name: 'Grey', hex: '#737373' },
  { name: 'Blue', hex: '#6AB8F2' },
  { name: 'Green', hex: '#6EE7A6' },
  { name: 'Amber', hex: '#F2C46A' },
  { name: 'Red', hex: '#F26A6A' },
  { name: 'Lime', hex: '#D9F26A' },
];

// Section header component for consistent styling
function SectionHeader({
  icon,
  label,
  expanded,
  onToggle,
  collapsible = false,
}: {
  icon: string;
  label: string;
  expanded?: boolean;
  onToggle?: () => void;
  collapsible?: boolean;
}) {
  const content = (
    <>
      <div className="flex items-center gap-2.5">
        <div className={classNames(icon, 'text-sm text-muted')} />
        <span className="text-[10px] font-medium text-muted uppercase tracking-[0.14em]">{label}</span>
      </div>
      {collapsible && (
        <div
          className={classNames(
            'i-ph:caret-down text-xs text-faint transition-transform duration-200',
            expanded && 'rotate-180',
          )}
        />
      )}
    </>
  );

  if (collapsible && onToggle) {
    return (
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center justify-between w-full py-2 hover:opacity-80 transition-opacity"
      >
        {content}
      </button>
    );
  }

  return <div className="flex items-center justify-between py-2">{content}</div>;
}

// Property row component
function PropertyRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-xs text-secondary flex-shrink-0">{label}</span>
      {children}
    </div>
  );
}

export function CanvasPropertyPanel({ chatId, onReloadPreview }: Props) {
  const selection = useStore($selectedElement);
  const deltas = useStore($styleDeltas);
  const [values, setValues] = useState<Record<string, string>>({});
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);

  // Text editing state
  const [textValue, setTextValue] = useState('');
  const [originalText, setOriginalText] = useState('');
  const [textEditStatus, setTextEditStatus] = useState<'checking' | 'editable' | 'not-editable' | 'none'>('none');
  const [textEditReason, setTextEditReason] = useState<string | null>(null);

  // Reseed values when selection changes
  useEffect(() => {
    const newValues = selection ? { ...selection.computedStyles } : {};
    setValues(newValues);
    setSaveState('idle');
    setSaveError(null);
    $styleDeltas.set({});

    // Reset text state
    const text = selection?.text ?? '';
    setTextValue(text);
    setOriginalText(text);

    // Only check editability if element has text AND is marked as editable by the selector
    if (!text) {
      setTextEditStatus('none');
      setTextEditReason(null);
    } else if (!selection?.editableText) {
      // Element has text but selector says it's not editable (e.g., complex JSX)
      setTextEditStatus('not-editable');
      setTextEditReason('Text contains complex JSX or expressions');
    } else {
      setTextEditStatus('checking');
      setTextEditReason(null);
    }
  }, [selection]);

  // Check if text is editable (exists verbatim in source)
  useEffect(() => {
    if (!selection?.text || textEditStatus !== 'checking') {
      return;
    }

    const checkTextEditable = async () => {
      const textTrim = selection.text?.trim();

      if (!textTrim || textTrim.length > 1000) {
        setTextEditStatus('not-editable');
        setTextEditReason('Text is empty or too long');

        return;
      }

      try {
        const content = await apiReadSandboxFile(chatId, selection.file);
        const occurrences = content.split(textTrim).length - 1;

        if (occurrences === 0) {
          setTextEditStatus('not-editable');
          setTextEditReason('Text not found in source (may contain HTML or line breaks)');
        } else if (occurrences > 1) {
          setTextEditStatus('not-editable');
          setTextEditReason(`Text appears ${occurrences} times in file (must be unique)`);
        } else {
          setTextEditStatus('editable');
          setTextEditReason(null);
        }
      } catch {
        setTextEditStatus('not-editable');
        setTextEditReason('Could not read source file');
      }
    };

    checkTextEditable();
  }, [chatId, selection, textEditStatus]);

  // Check if there are any pending changes (styles or text)
  const hasStyleChanges = Object.keys(deltas).length > 0;
  // Only count text changes if text is actually editable
  const hasTextChanges = textEditStatus === 'editable' && textValue.trim() !== originalText.trim();
  const hasPendingChanges = hasStyleChanges || hasTextChanges;

  // Save all changes (styles + text)
  const handleSave = async () => {
    if (!selection || !hasPendingChanges) {
      return;
    }

    setSaveState('saving');
    setSaveError(null);

    const errors: string[] = [];

    try {
      // Save style changes if any
      if (hasStyleChanges) {
        const styleResult = await applyStyleEditDirect(chatId, selection, deltas);
        trackVisualEdit('canvas', 'style', styleResult);

        if (!styleResult.ok) {
          console.error('[CanvasPropertyPanel] Style save failed:', styleResult.reason);
          errors.push(`Style: ${styleResult.reason}`);
          // Log failed style edit
          logStyleEditFailed(selection.tag, selection.file, selection.line, deltas, styleResult.reason);
        } else {
          // Log successful style edit with both before and after classes for revert capability
          // styleResult.classes contains the merged className now in the file
          logStyleEdit(
            chatId,
            selection.tag,
            selection.file,
            selection.line,
            selection.classes,
            styleResult.classes || selection.classes, // classesAfter
            deltas,
          );
        }
      }

      // Save text changes if any
      if (hasTextChanges && originalText) {
        const textResult = await applyTextEditDirect(chatId, selection, originalText, textValue);
        trackVisualEdit('canvas', 'text', textResult);

        if (!textResult.ok) {
          console.error('[CanvasPropertyPanel] Text save failed:', textResult.reason);
          errors.push(`Text: ${textResult.reason}`);
          // Log failed text edit
          logTextEditFailed(selection.tag, selection.file, selection.line, originalText, textValue, textResult.reason);
        } else {
          // Log successful text edit with chatId for revert capability
          logTextEdit(chatId, selection.tag, selection.file, selection.line, originalText, textValue);
          // Update original text to the new value after successful save
          setOriginalText(textValue.trim());
        }
      }

      if (errors.length === 0) {
        $styleDeltas.set({});
        setSaveState('saved');
        onReloadPreview();
        setTimeout(() => setSaveState('idle'), 2000);
      } else {
        setSaveError(errors.join('; '));
        setSaveState('error');
        setTimeout(() => {
          setSaveState('idle');
          setSaveError(null);
        }, 4000);
      }
    } catch (err) {
      console.error('[CanvasPropertyPanel] Save error:', err);
      setSaveError(err instanceof Error ? err.message : 'Unknown error');
      setSaveState('error');
      setTimeout(() => {
        setSaveState('idle');
        setSaveError(null);
      }, 4000);
    }
  };

  // Update a style value (no auto-save)
  const update = (camel: string, kebab: string, next: string) => {
    if (!selection) {
      return;
    }

    const was = selection.computedStyles[camel] ?? '';
    setValues((prev) => ({ ...prev, [camel]: next }));

    const nextDeltas: StyleDeltas = { ...$styleDeltas.get() };

    if (next === was) {
      delete nextDeltas[kebab];
    } else {
      nextDeltas[kebab] = { value: next, was };
    }

    $styleDeltas.set(nextDeltas);
  };

  const handleReset = () => {
    if (!selection) {
      return;
    }

    setValues({ ...selection.computedStyles });
    $styleDeltas.set({});
    setTextValue(originalText);
    setSaveState('idle');
    setSaveError(null);
  };

  const currentColorHex = cssColorToHex(values.color ?? '')?.toLowerCase() ?? null;
  const currentBgHex = cssColorToHex(values.backgroundColor ?? '')?.toLowerCase() ?? null;
  const currentBorderColorHex = cssColorToHex(values.borderColor ?? '')?.toLowerCase() ?? null;

  // Count total changes
  const changeCount = Object.keys(deltas).length + (hasTextChanges ? 1 : 0);

  return (
    <AnimatePresence mode="wait">
      {selection ? (
        <motion.div
          key="panel"
          initial={{ x: 340, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 340, opacity: 0 }}
          transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          className="w-full h-[50dvh] flex-shrink-0 flex flex-col bg-surface border-t border-hairline md:w-[320px] md:h-full md:border-t-0 md:border-l"
        >
          {/* Panel header */}
          <div className="flex-shrink-0 px-5 py-4 border-b border-hairline">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-5 h-5 rounded-md bg-accent/10 flex items-center justify-center">
                    <div className="i-ph:code text-accent text-xs" />
                  </div>
                  <code className="text-sm font-medium text-primary font-mono truncate">&lt;{selection.tag}&gt;</code>
                </div>
                <p className="text-[10px] text-muted truncate pl-7 font-mono">
                  {selection.file.split('/').pop()}:{selection.line}
                </p>
              </div>
              {hasPendingChanges && (
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-accent/10 border border-accent/20">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                  <span className="text-[10px] text-accent font-medium">{changeCount}</span>
                </div>
              )}
            </div>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-5 flex flex-col gap-6">
              {/* Text Content Section - show if element has text */}
              {selection.text && (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="i-ph:text-t text-sm text-muted" />
                      <span className="text-[10px] font-medium text-muted uppercase tracking-[0.14em]">
                        Text Content
                      </span>
                    </div>
                    {/* Status badge */}
                    {textEditStatus === 'checking' && (
                      <span className="flex items-center gap-1.5 text-[10px] text-muted">
                        <div className="i-svg-spinners:90-ring-with-bg text-xs" />
                      </span>
                    )}
                    {textEditStatus === 'editable' && (
                      <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-success/10 text-[10px] text-success">
                        <div className="i-ph:check text-xs" />
                        Editable
                      </span>
                    )}
                    {textEditStatus === 'not-editable' && (
                      <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-warning/10 text-[10px] text-warning">
                        <div className="i-ph:lock text-xs" />
                        Read-only
                      </span>
                    )}
                  </div>

                  {/* Editable textarea */}
                  {textEditStatus === 'editable' && (
                    <textarea
                      value={textValue}
                      onChange={(e) => setTextValue(e.target.value)}
                      placeholder="Enter text content..."
                      className={classNames(
                        'w-full rounded-xl bg-sunken border px-4 py-3 text-sm text-primary resize-none',
                        'placeholder:text-faint focus:outline-none focus:border-accent transition-colors',
                        hasTextChanges ? 'border-accent' : 'border-hairline',
                      )}
                      rows={3}
                    />
                  )}

                  {/* Read-only display */}
                  {textEditStatus === 'not-editable' && (
                    <div className="flex flex-col gap-2">
                      <div className="w-full rounded-xl bg-sunken/50 border border-hairline px-4 py-3 text-sm text-secondary italic">
                        "{selection.text.length > 80 ? `${selection.text.slice(0, 80)}...` : selection.text}"
                      </div>
                      {textEditReason && (
                        <p className="text-[10px] text-muted flex items-start gap-2 px-1">
                          <div className="i-ph:info text-xs mt-0.5 flex-shrink-0 text-muted" />
                          <span>{textEditReason}. Use chat to edit.</span>
                        </p>
                      )}
                    </div>
                  )}

                  {/* Loading state */}
                  {textEditStatus === 'checking' && (
                    <div className="w-full rounded-xl bg-sunken/30 border border-hairline px-4 py-3 text-sm text-muted animate-pulse">
                      Checking editability...
                    </div>
                  )}
                </div>
              )}

              {/* Color Section */}
              <div className="flex flex-col gap-4">
                <SectionHeader icon="i-ph:palette" label="Colors" />

                {/* Text Color */}
                <div className="flex flex-col gap-2.5">
                  <span className="text-xs text-secondary">Text Color</span>
                  <div className="flex items-center gap-2 flex-wrap">
                    {COLOR_SWATCHES.map((swatch) => (
                      <button
                        key={swatch.hex}
                        type="button"
                        onClick={() => update('color', 'color', swatch.hex)}
                        className={classNames(
                          'w-7 h-7 rounded-lg transition-all duration-150',
                          'hover:scale-110 hover:shadow-lg',
                          currentColorHex === swatch.hex.toLowerCase()
                            ? 'ring-2 ring-accent ring-offset-2 ring-offset-surface'
                            : 'ring-1 ring-inset ring-white/10',
                        )}
                        style={{ backgroundColor: swatch.hex }}
                        title={swatch.name}
                      />
                    ))}
                    <ColorField value={values.color ?? ''} onChange={(c) => update('color', 'color', c)} />
                  </div>
                </div>

                {/* Background Color */}
                <div className="flex flex-col gap-2.5">
                  <span className="text-xs text-secondary">Background</span>
                  <div className="flex items-center gap-2 flex-wrap">
                    {COLOR_SWATCHES.map((swatch) => (
                      <button
                        key={swatch.hex}
                        type="button"
                        onClick={() => update('backgroundColor', 'background-color', swatch.hex)}
                        className={classNames(
                          'w-7 h-7 rounded-lg transition-all duration-150',
                          'hover:scale-110 hover:shadow-lg',
                          currentBgHex === swatch.hex.toLowerCase()
                            ? 'ring-2 ring-accent ring-offset-2 ring-offset-surface'
                            : 'ring-1 ring-inset ring-white/10',
                        )}
                        style={{ backgroundColor: swatch.hex }}
                        title={swatch.name}
                      />
                    ))}
                    <ColorField
                      value={values.backgroundColor ?? ''}
                      onChange={(c) => update('backgroundColor', 'background-color', c)}
                    />
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-hairline" />

              {/* Typography Section */}
              <div className="flex flex-col gap-4">
                <SectionHeader icon="i-ph:text-aa" label="Typography" />

                <PropertyRow label="Size">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={values.fontSize?.replace('px', '') ?? ''}
                      onChange={(e) => update('fontSize', 'font-size', e.target.value)}
                      className="w-16 rounded-lg bg-sunken border border-hairline px-3 py-2 text-right font-mono text-xs text-primary focus:outline-none focus:border-accent transition-colors"
                    />
                    <span className="text-[10px] text-faint w-4">px</span>
                  </div>
                </PropertyRow>

                <PropertyRow label="Weight">
                  <select
                    value={values.fontWeight ?? ''}
                    onChange={(e) => update('fontWeight', 'font-weight', e.target.value)}
                    className="w-28 rounded-lg bg-sunken border border-hairline px-3 py-2 text-xs text-primary focus:outline-none focus:border-accent transition-colors cursor-pointer"
                  >
                    {WEIGHTS.map((w) => (
                      <option key={w.value} value={w.value}>
                        {w.label}
                      </option>
                    ))}
                  </select>
                </PropertyRow>

                <PropertyRow label="Align">
                  <div className="flex items-center gap-1 p-1 rounded-lg bg-sunken border border-hairline">
                    {ALIGNS.map((align) => (
                      <button
                        key={align.value}
                        type="button"
                        onClick={() => update('textAlign', 'text-align', align.value)}
                        className={classNames(
                          'w-8 h-7 rounded-md flex items-center justify-center transition-all duration-150',
                          values.textAlign === align.value
                            ? 'bg-accent text-black'
                            : 'text-secondary hover:text-primary hover:bg-elevated',
                        )}
                        title={align.value}
                      >
                        <div className={classNames(align.icon, 'text-sm')} />
                      </button>
                    ))}
                  </div>
                </PropertyRow>
              </div>

              {/* Divider */}
              <div className="h-px bg-hairline" />

              {/* Spacing Section */}
              <div className="flex flex-col gap-4">
                <SectionHeader icon="i-ph:arrows-out-cardinal" label="Spacing" />

                {/* Margin */}
                <div className="flex flex-col gap-2.5">
                  <span className="text-[10px] text-muted uppercase tracking-wide">Margin</span>
                  <div className="grid grid-cols-4 gap-2">
                    {(['Top', 'Right', 'Bottom', 'Left'] as const).map((dir) => (
                      <div key={dir} className="flex flex-col gap-1.5">
                        <span className="text-[9px] text-faint text-center uppercase">{dir[0]}</span>
                        <input
                          type="text"
                          value={values[`margin${dir}`]?.replace('px', '') ?? ''}
                          onChange={(e) => update(`margin${dir}`, `margin-${dir.toLowerCase()}`, e.target.value)}
                          className="w-full rounded-lg bg-sunken border border-hairline px-2 py-1.5 text-center font-mono text-[11px] text-primary focus:outline-none focus:border-accent transition-colors"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Padding */}
                <div className="flex flex-col gap-2.5">
                  <span className="text-[10px] text-muted uppercase tracking-wide">Padding</span>
                  <div className="grid grid-cols-4 gap-2">
                    {(['Top', 'Right', 'Bottom', 'Left'] as const).map((dir) => (
                      <div key={dir} className="flex flex-col gap-1.5">
                        <span className="text-[9px] text-faint text-center uppercase">{dir[0]}</span>
                        <input
                          type="text"
                          value={values[`padding${dir}`]?.replace('px', '') ?? ''}
                          onChange={(e) => update(`padding${dir}`, `padding-${dir.toLowerCase()}`, e.target.value)}
                          className="w-full rounded-lg bg-sunken border border-hairline px-2 py-1.5 text-center font-mono text-[11px] text-primary focus:outline-none focus:border-accent transition-colors"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-hairline" />

              {/* Border Section */}
              <div className="flex flex-col gap-4">
                <SectionHeader icon="i-ph:square" label="Border" />

                {/* Border Color */}
                <div className="flex flex-col gap-2.5">
                  <span className="text-xs text-secondary">Color</span>
                  <div className="flex items-center gap-2 flex-wrap">
                    {COLOR_SWATCHES.map((swatch) => (
                      <button
                        key={swatch.hex}
                        type="button"
                        onClick={() => update('borderColor', 'border-color', swatch.hex)}
                        className={classNames(
                          'w-6 h-6 rounded-md transition-all duration-150',
                          'hover:scale-110',
                          currentBorderColorHex === swatch.hex.toLowerCase()
                            ? 'ring-2 ring-accent ring-offset-1 ring-offset-surface'
                            : 'ring-1 ring-inset ring-white/10',
                        )}
                        style={{ backgroundColor: swatch.hex }}
                        title={swatch.name}
                      />
                    ))}
                    <ColorField
                      value={values.borderColor ?? ''}
                      onChange={(c) => update('borderColor', 'border-color', c)}
                    />
                  </div>
                </div>

                <PropertyRow label="Width">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={values.borderWidth?.replace('px', '') ?? ''}
                      onChange={(e) => update('borderWidth', 'border-width', e.target.value)}
                      className="w-14 rounded-lg bg-sunken border border-hairline px-3 py-2 text-right font-mono text-xs text-primary focus:outline-none focus:border-accent transition-colors"
                    />
                    <span className="text-[10px] text-faint w-4">px</span>
                  </div>
                </PropertyRow>

                <PropertyRow label="Radius">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={values.borderRadius?.replace('px', '') ?? ''}
                      onChange={(e) => update('borderRadius', 'border-radius', e.target.value)}
                      className="w-14 rounded-lg bg-sunken border border-hairline px-3 py-2 text-right font-mono text-xs text-primary focus:outline-none focus:border-accent transition-colors"
                    />
                    <span className="text-[10px] text-faint w-4">px</span>
                  </div>
                </PropertyRow>
              </div>

              {/* Divider */}
              <div className="h-px bg-hairline" />

              {/* Effects Section */}
              <div className="flex flex-col gap-4">
                <SectionHeader icon="i-ph:sparkle" label="Effects" />

                <PropertyRow label="Opacity">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={Math.round(parseFloat(values.opacity ?? '1') * 100)}
                      onChange={(e) => {
                        const pct = parseFloat(e.target.value);

                        if (!isNaN(pct)) {
                          update('opacity', 'opacity', String(pct / 100));
                        }
                      }}
                      className="w-14 rounded-lg bg-sunken border border-hairline px-3 py-2 text-right font-mono text-xs text-primary focus:outline-none focus:border-accent transition-colors"
                    />
                    <span className="text-[10px] text-faint w-4">%</span>
                  </div>
                </PropertyRow>
              </div>
            </div>
          </div>

          {/* Fixed bottom action bar */}
          <div className="flex-shrink-0 p-5 border-t border-hairline bg-surface">
            {/* Status messages */}
            <AnimatePresence mode="wait">
              {saveState === 'saved' && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-success/10 border border-success/20"
                >
                  <div className="i-ph:check-circle text-sm text-success" />
                  <span className="text-xs text-success">Changes saved!</span>
                </motion.div>
              )}
              {saveState === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="flex flex-col gap-1 mb-4 px-3 py-2 rounded-lg bg-danger/10 border border-danger/20"
                >
                  <div className="flex items-center gap-2">
                    <div className="i-ph:warning-circle text-sm text-danger" />
                    <span className="text-xs text-danger font-medium">Save failed</span>
                  </div>
                  {saveError && <span className="text-[10px] text-muted pl-5">{saveError}</span>}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Action buttons */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleReset}
                disabled={!hasPendingChanges || saveState === 'saving'}
                className={classNames(
                  'flex-1 flex items-center justify-center gap-2 h-11 rounded-xl text-sm font-medium transition-all duration-200',
                  hasPendingChanges && saveState !== 'saving'
                    ? 'bg-raised hover:bg-elevated border border-hairline text-secondary hover:text-primary'
                    : 'bg-raised/50 border border-hairline/50 text-faint cursor-not-allowed',
                )}
              >
                <div className="i-ph:arrow-counter-clockwise text-sm" />
                Reset
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!hasPendingChanges || saveState === 'saving'}
                className={classNames(
                  'flex-1 flex items-center justify-center gap-2 h-11 rounded-xl text-sm font-medium transition-all duration-200',
                  hasPendingChanges && saveState !== 'saving'
                    ? 'bg-accent hover:bg-accent/90 text-black shadow-[0_4px_12px_-2px_rgba(217,242,106,0.3)]'
                    : 'bg-accent/20 text-accent/40 cursor-not-allowed',
                )}
              >
                {saveState === 'saving' ? (
                  <>
                    <div className="i-svg-spinners:90-ring-with-bg text-sm" />
                    Saving...
                  </>
                ) : (
                  <>
                    <div className="i-ph:floppy-disk text-sm" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      ) : (
        /* Empty state when no element selected */
        <motion.div
          key="empty"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          // Phones give the preview the full height until something is selected.
          className="hidden md:flex w-[320px] h-full bg-surface border-l border-hairline flex-col"
        >
          {/* Header placeholder */}
          <div className="flex-shrink-0 px-5 py-4 border-b border-hairline">
            <div className="flex items-center gap-2.5">
              <div className="w-5 h-5 rounded-md bg-raised flex items-center justify-center">
                <div className="i-ph:cursor-click text-muted text-xs" />
              </div>
              <span className="text-sm text-muted">Properties</span>
            </div>
          </div>

          {/* Empty state content */}
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-raised/50 flex items-center justify-center mx-auto mb-5">
                <div className="i-ph:hand-pointing text-3xl text-faint" />
              </div>
              <h3 className="text-sm font-medium text-secondary mb-2">No element selected</h3>
              <p className="text-xs text-muted leading-relaxed max-w-[200px] mx-auto">
                Click any element in the preview to inspect and edit its styles
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
